import type { ComponentProps } from "react";
import { t } from "ttag";

import {
  CANCEL_SUBSCRIPTION_MODAL_STEPS,
  useCancelSubscriptionContext,
} from "@/features/billing/context";
import {
  useApplyCancellationDiscount,
  useCancellationDiscount,
} from "@/features/billing/hooks";
import { useSendCancellationRequest } from "@/features/billing/hooks/use-send-cancellation-request";
import { LogoLoader } from "@/shared/components";
import {
  Banner,
  Button,
  ConfettiMinimalisticIcon,
  Dialog,
  useToastStore,
} from "@/shared/ui";

export const CancellationDiscountModal = ({
  open,
  onOpenChange,
}: ComponentProps<typeof Dialog.Root>) => {
  const pushToast = useToastStore((state) => state.pushToast);

  const {
    subscription,
    reasons,
    actions: { setCurrentModal },
  } = useCancelSubscriptionContext();

  const { data: discount } = useCancellationDiscount({
    subscriptionId: subscription.id,
  });

  const applyDiscountMutation = useApplyCancellationDiscount();
  const sendCancellationRequest = useSendCancellationRequest();

  const handleApplyDiscount = () => {
    applyDiscountMutation.mutate(
      {
        subscriptionId: subscription.id,
        cancellationReasons: reasons,
      },
      {
        onSuccess: () => {
          onOpenChange?.(false);
          void pushToast({
            type: "success",
            title: t`Success`,
            message: t`Subscription discount successfully applied!`,
          });
        },
      },
    );
  };

  const handleContinue = () => {
    sendCancellationRequest.mutate(subscription.id, {
      onSuccess: () => {
        setCurrentModal(CANCEL_SUBSCRIPTION_MODAL_STEPS.EMAIL_SENT);
      },
    });
  };

  if (!discount?.credit) {
    return null;
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <LogoLoader
        show={
          applyDiscountMutation.isPending || sendCancellationRequest.isPending
        }
        fullscreen
      />

      <Dialog.Content>
        <Dialog.Header icon={ConfettiMinimalisticIcon}>
          <Dialog.Title>{t`We'd love for you to stay!`}</Dialog.Title>
          <Dialog.Description>
            {t`We're offering you a special deal to explore LevelsRx to its full potential. If you have questions or need support, our team is here to help!`}
          </Dialog.Description>
        </Dialog.Header>

        <Banner.Root>
          <Banner.Header className="flex-row justify-center">
            <ConfettiMinimalisticIcon className="mt-1.5 hidden size-4 md:block" />
            <Banner.Title>
              {t`$${discount.credit.value} off your next billing cycle!`}
            </Banner.Title>
          </Banner.Header>
        </Banner.Root>

        <Dialog.Footer>
          <Button size="lg" variant="secondary" onClick={handleApplyDiscount}>
            {t`Yes, I'm in!`}
          </Button>
          <Button size="lg" onClick={handleContinue}>{t`No, thanks`}</Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
